import React, { useEffect, useState } from "react";
import AppShell from "../components/AppShell";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { ROLES } from "../lib/rbac";
import { Shield, Database, KeyRound, ArrowRight, BookOpen, FileText, Download, Trash2, Wrench, ShieldCheck, Bell, RefreshCw, Timer, Unlock, ScrollText, CalendarClock, HeartPulse } from "lucide-react";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import {
    Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from "../components/ui/dialog";
import { Button } from "../components/ui/button";

const CARDS = [
                    {
                        key: "permissions",
                        title: "Permissions & Overrides",
                        description: "Grant or revoke per-user permissions beyond their base role.",
                        href: "/admin/settings/permissions",
                        icon: KeyRound,
                        roles: [ROLES.KAAFI_ADMIN],
                    },
    {
        key: "notifications",
        title: "Notification Recipients",
        description:
            "Configure who receives automated email notifications when specific events happen (e.g. payroll marked as paid).",
        href: "/admin/notifications",
        icon: Bell,
        roles: [ROLES.KAAFI_ADMIN],
    },
    {
        key: "leave-types",
        title: "Leave Types (Somali Labor Law)",
        description:
            "Edit entitlement, tenure gate, gender restriction, evidence, and min/max days for every leave type. Article references (Article 69, 70, 71, 129, 130) reflect the law and are locked.",
        href: "/admin/settings/leave-types",
        icon: ScrollText,
        roles: [ROLES.KAAFI_ADMIN, ROLES.KAAFI_HR],
    },
    {
        key: "leave-accrual",
        title: "Leave Accrual Config",
        description:
            "Configure the fiscal reset date, carry-over policy (capped / reset / payout), and cap days for the 2.5-days-per-month Annual Leave accrual. Somali Labor Law Article 69.",
        href: "/admin/settings/leave-accrual",
        icon: CalendarClock,
        roles: [ROLES.KAAFI_ADMIN],
    },
    {
        key: "sick-pay",
        title: "Sick Pay Config (Article 70)",
        description:
            "Configure the tiered sick-pay percentages (100% / 75% / 50% / contract), probation defaults, short-illness rule, and medical-certificate requirement.",
        href: "/admin/settings/sick-pay",
        icon: HeartPulse,
        roles: [ROLES.KAAFI_ADMIN],
    },
    {
        key: "demo-data",
        title: "Demo Data",
        description:
            "Wipe all business data and reseed with a Somali-centric demo dataset for predictable demos.",
        href: "/admin/settings/demo-data",
        icon: Database,
        roles: [ROLES.KAAFI_ADMIN],
        accent: true,
    },
];

const DOCS = [
    {
        key: "biometric-usage-guide",
        label: "Biometric Devices — First-Time Setup & Daily Use Guide",
        description:
            "Beginner-friendly walkthrough for System Admins registering a new ZKTeco or Hikvision device, enrolling employees, and understanding how attendance flows end-to-end.",
        href: "/ASL_Biometric_FirstTime_Usage_Guide.docx",
        testid: "admin-settings-download-biometric-usage",
    },
    {
        key: "setup-guide",
        label: "ZKTeco PRO 200 Setup Guide (field deployment)",
        description:
            "Step-by-step manual for configuring the physical ZKTeco PRO 200 devices, ADMS config, employee enrollment and troubleshooting.",
        href: "/ASL_ZKTeco_PRO200_Setup_Guide.docx",
        testid: "admin-settings-download-setup-guide",
    },
    {
        key: "training-manual",
        label: "Training Manual (team training)",
        description:
            "Hands-on training manual with module-by-module scenarios, payroll math, edge cases and a sign-off rubric.",
        href: "/ASL_HR_System_Training_Manual.docx",
        testid: "admin-settings-download-training",
    },
    {
        key: "hikvision-setup-guide",
        label: "Hikvision DS-K1T342 Setup Guide (field deployment)",
        description:
            "Field runbook for the Hikvision DS-K1T342MFWX-E1 face terminal — network setup, ISAPI Listening Host config, employee enrollment, troubleshooting.",
        href: "/ASL_Hikvision_DS-K1T342MFWX_Setup_Guide.docx",
        testid: "admin-settings-download-hikvision-setup-guide",
    },
    {
        key: "testing-guide",
        label: "Testing Guide (one-time)",
        description:
            "Step-by-step QA checklist for verifying every screen and workflow against a freshly seeded dataset.",
        href: "/ASL_HR_System_Testing_Guide.docx",
        testid: "admin-settings-download-testing",
    },
];

export default function AdminSettings() {
    const { user } = useAuth();
    const navigate = useNavigate();
    const allowed = (card) => card.roles.includes(user?.role);
    const cards = CARDS.filter(allowed);
    const isAdmin = user?.role === ROLES.KAAFI_ADMIN;

    // Documentation state — { filename: exists } map from the backend. Cards
    // whose file no longer exists on disk are silently hidden.
    const [docsExist, setDocsExist] = useState({});
    const [confirmDoc, setConfirmDoc] = useState(null); // {key, label, filename}
    const [deleting, setDeleting] = useState(false);

    // --- Realign Biometric IDs state ---
    // realignPreview: null when idle, or { changes, count, safelisted_skipped }
    const [realignPreview, setRealignPreview] = useState(null);
    const [realignLoading, setRealignLoading] = useState(false);
    const [realignApplying, setRealignApplying] = useState(false);

    // --- Iter60.p27 AP recompute state ---
    // p27Preview: null when idle, or the full endpoint payload
    // (processed / changed_count / sample_diffs / ...).
    const [p27Preview, setP27Preview] = useState(null);
    const [p27Loading, setP27Loading] = useState(false);
    const [p27Applying, setP27Applying] = useState(false);
    const [p27ConfirmApply, setP27ConfirmApply] = useState(false);

    // --- Iter60.p34 Bulk Unapprove IC + CN state ---
    const [p34Preview, setP34Preview] = useState(null);
    const [p34Loading, setP34Loading] = useState(false);
    const [p34Applying, setP34Applying] = useState(false);
    const [p34ConfirmApply, setP34ConfirmApply] = useState(false);

    // --- Iter60.p39 Recompute worked_minutes state ---
    const [p39Preview, setP39Preview] = useState(null);
    const [p39Loading, setP39Loading] = useState(false);
    const [p39Applying, setP39Applying] = useState(false);
    const [p39ConfirmApply, setP39ConfirmApply] = useState(false);

    // --- Iter60.p45 Per-day schedule recompute state (IC + CN only) ---
    const [p45Preview, setP45Preview] = useState(null);
    const [p45Loading, setP45Loading] = useState(false);
    const [p45Applying, setP45Applying] = useState(false);
    const [p45ConfirmApply, setP45ConfirmApply] = useState(false);

    // --- Iter62 Clamp payable-base reclamp (all modules) ---
    const [p62Preview, setP62Preview] = useState(null);
    const [p62Loading, setP62Loading] = useState(false);
    const [p62Applying, setP62Applying] = useState(false);

    // --- Iter63 Schedule-bonus recompute (all modules) ---
    const [p63Preview, setP63Preview] = useState(null);
    const [p63Loading, setP63Loading] = useState(false);
    const [p63Applying, setP63Applying] = useState(false);

    // --- Iter83 P0 (2026-02) — Non-Casual bundling backfill state ---
    // Ops path: click Preview → see how many casual weekly periods
    // will be flagged/suppressed → click Apply to stamp them all.
    // Idempotent on the backend; safe to click multiple times.
    const [p83Preview, setP83Preview] = useState(null);
    const [p83Loading, setP83Loading] = useState(false);
    const [p83Applying, setP83Applying] = useState(false);
    const [p83ConfirmOpen, setP83ConfirmOpen] = useState(false);

    // --- Iter60.p51 Cleanup dev seed state ---
    // p51MatchCount: null (not fetched yet) or number of test workers
    // currently in the DB that will be deleted. Fetched on mount for
    // Admin so the button label + subtext are accurate.
    const [p51MatchCount, setP51MatchCount] = useState(null);
    const [p51ConfirmOpen, setP51ConfirmOpen] = useState(false);
    const [p51Applying, setP51Applying] = useState(false);
    const [p51Result, setP51Result] = useState(null);

    useEffect(() => {
        if (!isAdmin) return;
        (async () => {
            try {
                const { data } = await api.get("/admin/documentation/list");
                const map = {};
                (data || []).forEach((d) => { map[d.filename] = !!d.exists; });
                setDocsExist(map);
            } catch (e) {
                // Non-fatal: if list fails, show all cards (existing behaviour).
                const fallback = {};
                DOCS.forEach((d) => { fallback[d.href.replace(/^\//, "")] = true; });
                setDocsExist(fallback);
            }
        })();
    }, [isAdmin]);

    // Fetch p51 cleanup match count on mount (Admin only) so the UI can
    // show "N test workers will be removed" without a user click.
    useEffect(() => {
        if (!isAdmin) return;
        (async () => {
            try {
                const { data } = await api.get("/admin/maintenance/cleanup-p51-seed/preview");
                setP51MatchCount(typeof data?.match_count === "number" ? data.match_count : 0);
            } catch (e) {
                // Non-fatal — leave count as null; button will still work.
                setP51MatchCount(null);
            }
        })();
    }, [isAdmin]);

    const visibleDocs = DOCS.filter((d) => {
        const fn = d.href.replace(/^\//, "");
        // If we haven't fetched the list yet OR the file exists, show it.
        return docsExist[fn] !== false;
    });

    const doDelete = async () => {
        if (!confirmDoc) return;
        setDeleting(true);
        try {
            await api.delete(`/admin/documentation/${encodeURIComponent(confirmDoc.filename)}`);
            setDocsExist((m) => ({ ...m, [confirmDoc.filename]: false }));
            toast.success(`'${confirmDoc.label}' deleted.`);
            setConfirmDoc(null);
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setDeleting(false);
        }
    };

    // --- Realign Biometric IDs handlers ---
    const runRealignPreview = async () => {
        setRealignLoading(true);
        try {
            const { data } = await api.post("/admin/biometric-id-realign?dry_run=true");
            if ((data?.count || 0) === 0) {
                toast.success("All biometric IDs are correctly aligned. No changes needed.");
                setRealignPreview(null);
            } else {
                setRealignPreview(data);
            }
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setRealignLoading(false);
        }
    };

    const applyRealign = async () => {
        setRealignApplying(true);
        try {
            const { data } = await api.post("/admin/biometric-id-realign?dry_run=false");
            toast.success(`Realigned ${data.count} biometric ID${data.count === 1 ? "" : "s"}.`);
            setRealignPreview(null);
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setRealignApplying(false);
        }
    };

    // --- Iter60.p27 AP recompute handlers ---
    const runP27Preview = async () => {
        setP27Loading(true);
        try {
            const { data } = await api.post(
                "/admin/maintenance/iter60p27-recompute-ap-attendance",
                { dry_run: true },
            );
            if ((data?.changed_count || 0) === 0) {
                toast.success(
                    `All ${data?.processed || 0} AP rows are already aligned to the current shift rule.`,
                );
                setP27Preview(null);
            } else {
                setP27Preview(data);
            }
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setP27Loading(false);
        }
    };

    const applyP27 = async () => {
        setP27Applying(true);
        try {
            const { data } = await api.post(
                "/admin/maintenance/iter60p27-recompute-ap-attendance",
                { dry_run: false },
            );
            toast.success(
                `Recomputed ${data.changed_count} AP row${data.changed_count === 1 ? "" : "s"}.`,
            );
            setP27Preview(null);
            setP27ConfirmApply(false);
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setP27Applying(false);
        }
    };

    // --- Iter60.p34 handlers ---
    const runP34Preview = async () => {
        setP34Loading(true);
        try {
            const { data } = await api.post(
                "/admin/maintenance/iter60p34-bulk-unapprove-ic-cn",
                { dry_run: true },
            );
            if ((data?.would_unapprove || 0) === 0) {
                toast.success("No approved IC/CN rows found — nothing to unapprove.");
                setP34Preview(null);
            } else {
                setP34Preview(data);
            }
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setP34Loading(false);
        }
    };

    const applyP34 = async () => {
        setP34Applying(true);
        try {
            const { data } = await api.post(
                "/admin/maintenance/iter60p34-bulk-unapprove-ic-cn",
                { dry_run: false },
            );
            toast.success(
                `Unapproved ${data.total_updated || 0} row${(data.total_updated || 0) === 1 ? "" : "s"} across IC + CN.`,
            );
            setP34Preview(null);
            setP34ConfirmApply(false);
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setP34Applying(false);
        }
    };

    // --- Iter60.p39 handlers ---
    // Iter60.p41 — button now hits the p41 endpoint (same sweep,
    // clearer audit action). State names kept as `p39*` for
    // minimal-diff refactor.
    const runP39Preview = async () => {
        setP39Loading(true);
        try {
            const { data } = await api.post(
                "/admin/maintenance/iter60p41-recompute-with-break-paid",
                { dry_run: true },
            );
            if ((data?.changed_count || 0) === 0) {
                toast.success(
                    `All ${data?.processed || 0} attendance rows already match the canonical worked-minutes formula.`,
                );
                setP39Preview(null);
            } else {
                setP39Preview(data);
            }
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setP39Loading(false);
        }
    };

    const applyP39 = async () => {
        setP39Applying(true);
        try {
            const { data } = await api.post(
                "/admin/maintenance/iter60p41-recompute-with-break-paid",
                { dry_run: false },
            );
            const skipped = data.payroll_periods_skipped_paid?.length || 0;
            toast.success(
                `Recomputed ${data.changed_count} row${data.changed_count === 1 ? "" : "s"}. `
                + `Payroll periods recomputed: ${data.payroll_periods_recomputed?.length || 0}`
                + (skipped ? ` (${skipped} paid period${skipped === 1 ? "" : "s"} left untouched).` : "."),
            );
            setP39Preview(null);
            setP39ConfirmApply(false);
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setP39Applying(false);
        }
    };

    // Iter60.p45 — Per-day schedule recompute preview/apply (IC + CN).
    // Same dry-run → apply pattern as p39/p41. The backend only touches
    // rows whose weekday has a `per_day_overrides` entry on the
    // resolved rule.
    const runP45Preview = async () => {
        setP45Loading(true);
        try {
            const { data } = await api.post(
                "/admin/maintenance/iter60p45-per-day-schedule-recompute",
                { dry_run: true },
            );
            if ((data?.changed_count || 0) === 0) {
                toast.success(
                    `No IC/CN attendance rows need updating — every override is already applied.`,
                );
                setP45Preview(null);
            } else {
                setP45Preview(data);
            }
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setP45Loading(false);
        }
    };

    const applyP45 = async () => {
        setP45Applying(true);
        try {
            const { data } = await api.post(
                "/admin/maintenance/iter60p45-per-day-schedule-recompute",
                { dry_run: false },
            );
            const skipped = data.payroll_periods_skipped_paid?.length || 0;
            toast.success(
                `Recomputed ${data.changed_count} IC/CN row${data.changed_count === 1 ? "" : "s"}. `
                + `Payroll periods recomputed: ${data.payroll_periods_recomputed?.length || 0}`
                + (skipped ? ` (${skipped} paid period${skipped === 1 ? "" : "s"} left untouched).` : "."),
            );
            setP45Preview(null);
            setP45ConfirmApply(false);
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setP45Applying(false);
        }
    };

    // Iter62 — Re-derive `payable_base_minutes` on every non-terminal
    // attendance row. Dry-run first so the admin can preview the CN /
    // IC / AP breakdown before touching anything.
    const runP62Preview = async () => {
        setP62Loading(true);
        try {
            const { data } = await api.post(
                "/admin/maintenance/iter62-reclamp-payable-minutes",
                { dry_run: true, recompute_lines: false },
            );
            if ((data?.updated || 0) === 0) {
                toast.success(
                    `All ${data?.unchanged || 0} attendance rows already have the correct payable base (Clamp toggle applied).`,
                );
                setP62Preview(null);
            } else {
                setP62Preview(data);
            }
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setP62Loading(false);
        }
    };

    const applyP62 = async () => {
        setP62Applying(true);
        try {
            const { data } = await api.post(
                "/admin/maintenance/iter62-reclamp-payable-minutes",
                { dry_run: false, recompute_lines: true },
            );
            toast.success(
                `Reclamped ${data.updated || 0} row${(data.updated || 0) === 1 ? "" : "s"}. `
                + `Skipped ${data.skipped_terminal || 0} paid/funded/closed. `
                + `Refreshed ${data.periods_refreshed_actual || 0} payroll period${(data.periods_refreshed_actual || 0) === 1 ? "" : "s"}.`,
            );
            setP62Preview(null);
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setP62Applying(false);
        }
    };

    // Iter63 — Refresh schedule bonus fields on every non-terminal row.
    // Reuses the same reclamp helper as Iter62; the sweep is idempotent
    // and covers CN / IC / AP in one pass.
    const runP63Preview = async () => {
        setP63Loading(true);
        try {
            const { data } = await api.post(
                "/admin/maintenance/iter63-schedule-bonus-recompute",
                { dry_run: true, recompute_lines: false },
            );
            if ((data?.updated || 0) === 0) {
                toast.success(
                    `All ${data?.unchanged || 0} attendance rows already have the correct schedule bonus.`,
                );
                setP63Preview(null);
            } else {
                setP63Preview(data);
            }
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setP63Loading(false);
        }
    };

    const applyP63 = async () => {
        setP63Applying(true);
        try {
            const { data } = await api.post(
                "/admin/maintenance/iter63-schedule-bonus-recompute",
                { dry_run: false, recompute_lines: true },
            );
            toast.success(
                `Refreshed ${data.updated || 0} row${(data.updated || 0) === 1 ? "" : "s"}. `
                + `Skipped ${data.skipped_terminal || 0} paid/funded/closed. `
                + `Payroll periods refreshed: ${data.periods_refreshed_actual || 0}.`,
            );
            setP63Preview(null);
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setP63Applying(false);
        }
    };

    // --- Iter83 P0 (2026-02) — Non-Casual bundling backfill handlers ---
    // Dry-run first for a preview modal that shows counts. On apply,
    // sends `dry_run:false` — the backend stamps every casual weekly
    // period with the corrected `is_last_casual_week_of_month` +
    // `bundles_non_casual_monthly` flags. Idempotent.
    const runP83Preview = async () => {
        setP83Loading(true);
        try {
            const { data } = await api.post(
                "/admin/maintenance/iter83-bundling-backfill",
                { dry_run: true },
            );
            setP83Preview(data);
            setP83ConfirmOpen(true);
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setP83Loading(false);
        }
    };

    const applyP83 = async () => {
        setP83Applying(true);
        try {
            const { data } = await api.post(
                "/admin/maintenance/iter83-bundling-backfill",
                { dry_run: false },
            );
            const suppressed = data?.periods_suppressed || 0;
            const bundled = data?.periods_bundled || 0;
            toast.success(
                `Scanned ${data?.periods_scanned || 0} casual weekly period${(data?.periods_scanned || 0) === 1 ? "" : "s"}. `
                + `Stamped ${data?.periods_stamped || 0}. `
                + `${suppressed} period${suppressed === 1 ? "" : "s"} suppressed (paid sibling), `
                + `${bundled} still bundling (unpaid sibling or missing).`,
            );
            setP83Preview(data);
            setP83ConfirmOpen(false);
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setP83Applying(false);
        }
    };

    // --- Iter60.p51 Cleanup dev-seed workers handler ---
    // The endpoint is idempotent and hard-caps at 100 candidates. Runs
    // the delete + auto-recomputes totals for every period the seed
    // workers had lines in.
    const applyP51Cleanup = async () => {
        setP51Applying(true);
        try {
            const { data } = await api.post("/admin/maintenance/cleanup-p51-seed");
            setP51Result(data);
            const d = data?.deleted || {};
            const recomputed = data?.periods_recomputed?.length || 0;
            toast.success(
                `Removed ${d.employees || 0} test worker${(d.employees || 0) === 1 ? "" : "s"} `
                + `and ${d.payroll_lines || 0} payroll line${(d.payroll_lines || 0) === 1 ? "" : "s"}. `
                + `Recomputed ${recomputed} period${recomputed === 1 ? "" : "s"}.`,
            );
            setP51MatchCount(0);
            setP51ConfirmOpen(false);
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setP51Applying(false);
        }
    };

    return (
        <AppShell title="System Settings">
            <section className="space-y-6" data-testid="admin-settings-page">
                <header className="flex items-center gap-3">
                    <Shield className="h-6 w-6 asl-navy-text" />
                    <div>
                        <h2 className="text-xl font-bold asl-navy-text">System Settings</h2>
                        <p className="text-sm text-[color:var(--asl-muted)]">
                            Administrative tools for the ASL HR platform.
                        </p>
                    </div>
                </header>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="admin-settings-grid">
                    {cards.map((c) => {
                        const Icon = c.icon;
                        return (
                            <button
                                key={c.key}
                                onClick={() => navigate(c.href)}
                                className="asl-card p-5 text-left transition-shadow hover:shadow-md focus:outline-none focus:ring-2 focus:ring-[color:var(--asl-navy)]"
                                data-testid={`admin-settings-card-${c.key}`}
                            >
                                <div className="flex items-start gap-3">
                                    <div
                                        className={`p-2 rounded-md ${
                                            c.accent ? "asl-crimson-bg text-white" : "asl-navy-bg text-white"
                                        }`}
                                    >
                                        <Icon className="h-5 w-5" />
                                    </div>
                                    <div className="flex-1">
                                        <div className="flex items-center justify-between">
                                            <h3 className="text-base font-bold asl-navy-text">{c.title}</h3>
                                            <ArrowRight className="h-4 w-4 text-[color:var(--asl-muted)]" />
                                        </div>
                                        <p className="text-sm text-[color:var(--asl-muted)] mt-1">
                                            {c.description}
                                        </p>
                                    </div>
                                </div>
                            </button>
                        );
                    })}
                </div>

                {/* System Tools (Admin only) */}
                {isAdmin && (
                    <div className="asl-card p-5" data-testid="admin-settings-system-tools-card">
                        <div className="flex items-start gap-3 mb-4">
                            <div className="p-2 asl-navy-bg text-white rounded-md">
                                <Wrench className="h-5 w-5" />
                            </div>
                            <div>
                                <h3 className="text-base font-bold asl-navy-text">System Tools</h3>
                                <p className="text-sm text-[color:var(--asl-muted)] mt-1">
                                    One-shot administrative utilities for data-integrity cleanup.
                                </p>
                            </div>
                        </div>
                        <div className="space-y-3">
                            <div
                                className="flex items-center justify-between gap-3 flex-wrap"
                                data-testid="admin-settings-tool-realign"
                            >
                                <div className="flex items-start gap-3 flex-1 min-w-[240px]">
                                    <ShieldCheck className="h-4 w-4 asl-navy-text mt-0.5 shrink-0" />
                                    <div>
                                        <div className="text-sm font-semibold asl-navy-text">Realign Biometric IDs</div>
                                        <p className="text-xs text-[color:var(--asl-muted)] mt-0.5">
                                            Scans for employees whose <span className="mono">biometric_user_id</span> prefix
                                            disagrees with the module rule (AP=1xxx, IC=2xxx, CN=3xxx) and re-derives them
                                            from the employee code. Legacy safelist (<span className="mono">88</span>,
                                            <span className="mono"> 777</span>, <span className="mono">99</span>) is skipped.
                                            Shows a preview before applying.
                                        </p>
                                    </div>
                                </div>
                                <Button
                                    onClick={runRealignPreview}
                                    disabled={realignLoading}
                                    className="asl-navy-bg text-white hover:opacity-90"
                                    data-testid="admin-realign-btn"
                                >
                                    {realignLoading ? "Scanning…" : "Realign Biometric IDs"}
                                </Button>
                            </div>
                            <div
                                className="flex items-center justify-between gap-3 flex-wrap border-t border-[color:var(--asl-border,#E5E7EB)] pt-3"
                                data-testid="admin-settings-tool-p27"
                            >
                                <div className="flex items-start gap-3 flex-1 min-w-[240px]">
                                    <RefreshCw className="h-4 w-4 asl-navy-text mt-0.5 shrink-0" />
                                    <div>
                                        <div className="text-sm font-semibold asl-navy-text">
                                            Recompute AP Attendance (Iter60.p27)
                                        </div>
                                        <p className="text-xs text-[color:var(--asl-muted)] mt-0.5">
                                            Realigns every AP Camp <span className="mono">attendance_daily</span> row to the
                                            currently-active shift rule. Fixes stale weekend/holiday multipliers, sets
                                            <span className="mono"> weekend_present</span> on Fridays with worked minutes,
                                            and back-fills <span className="mono">shift_rule_id</span>. Preserves manual
                                            clock times, <span className="mono">worked_minutes</span>, and locked statuses
                                            (<span className="mono">on_leave</span>, <span className="mono">exception</span>,
                                            <span className="mono"> pending</span>). Dry-run first.
                                        </p>
                                    </div>
                                </div>
                                <Button
                                    onClick={runP27Preview}
                                    disabled={p27Loading}
                                    className="asl-navy-bg text-white hover:opacity-90"
                                    data-testid="admin-p27-recompute-btn"
                                >
                                    {p27Loading ? "Scanning…" : "Run AP Attendance Recompute"}
                                </Button>
                            </div>
                            <div
                                className="flex items-center justify-between gap-3 flex-wrap border-t border-[color:var(--asl-border,#E5E7EB)] pt-3"
                                data-testid="admin-settings-tool-p39"
                            >
                                <div className="flex items-start gap-3 flex-1 min-w-[240px]">
                                    <Timer className="h-4 w-4 asl-navy-text mt-0.5 shrink-0" />
                                    <div>
                                        <div className="text-sm font-semibold asl-navy-text">
                                            Run Worked-Minutes Recompute (Break-Is-Paid, Iter60.p41)
                                        </div>
                                        <p className="text-xs text-[color:var(--asl-muted)] mt-0.5">
                                            Applies the canonical worked-minutes helper with the
                                            new business rule where <strong>break time is paid</strong>
                                            (included in worked hours). The helper reads
                                            <span className="mono"> break_is_paid</span> from the
                                            row's <span className="mono">shift_rule_snapshot</span>
                                            or the module's active shift rule (default <strong>true</strong>
                                            after the p41 backfill). On apply, sweeps every attendance
                                            row across IC/AP/CN, rewrites <span className="mono">worked_minutes</span>
                                            + <span className="mono">payable_hours</span>, and triggers
                                            payroll recompute for affected draft/prepared periods.
                                            <br />
                                            <span className="asl-navy-text font-semibold">
                                                Paid periods are left untouched
                                            </span> (historical) — the response reports how many were skipped.
                                            <span className="asl-crimson-text font-semibold"> Always dry-run
                                            first and review the diffs.</span>
                                        </p>
                                    </div>
                                </div>
                                <Button
                                    onClick={runP39Preview}
                                    disabled={p39Loading}
                                    className="asl-navy-bg text-white hover:opacity-90"
                                    data-testid="admin-p39-recompute-btn"
                                >
                                    {p39Loading ? "Scanning…" : "Run Worked-Minutes Recompute (Break-Is-Paid)"}
                                </Button>
                            </div>
                            {/* Iter60.p45 — Per-day schedule recompute
                                (IC + CN only). Skips rows whose weekday
                                has no `per_day_overrides` entry so this
                                is cheap to dry-run frequently. */}
                            <div
                                className="flex items-center justify-between gap-3 flex-wrap border-t border-[color:var(--asl-border,#E5E7EB)] pt-3"
                                data-testid="admin-settings-tool-p45"
                            >
                                <div className="flex items-start gap-3 flex-1 min-w-[240px]">
                                    <Timer className="h-4 w-4 asl-navy-text mt-0.5 shrink-0" />
                                    <div>
                                        <div className="text-sm font-semibold asl-navy-text">
                                            Run Per-Day Schedule Recompute (Iter60.p45)
                                        </div>
                                        <p className="text-xs text-[color:var(--asl-muted)] mt-0.5">
                                            Sweeps every <strong>IC + CN</strong> attendance row
                                            whose weekday has a{" "}
                                            <span className="mono">per_day_overrides</span>{" "}
                                            entry on the resolved shift rule (e.g. Thursday's
                                            early-finish end time) and re-derives{" "}
                                            <span className="mono">worked_minutes</span> /
                                            <span className="mono"> late_minutes</span> /
                                            <span className="mono"> overtime_minutes</span> /
                                            <span className="mono"> payable_hours</span> under
                                            the override values. Rows outside an override day
                                            are skipped for free — this sweep is safe to run
                                            after every per-day rule edit.
                                            <br />
                                            <span className="asl-navy-text font-semibold">
                                                Paid payroll periods are left untouched
                                            </span> (historical).
                                            <span className="asl-crimson-text font-semibold"> Always
                                            dry-run first and review the diffs.</span>
                                        </p>
                                    </div>
                                </div>
                                <Button
                                    onClick={runP45Preview}
                                    disabled={p45Loading}
                                    className="asl-navy-bg text-white hover:opacity-90"
                                    data-testid="admin-p45-recompute-btn"
                                >
                                    {p45Loading ? "Scanning…" : "Run Per-Day Schedule Recompute"}
                                </Button>
                            </div>
                            {/* Iter62 — Reclamp payable-base minutes.
                                WORKED stays raw; PAYABLE clamps to the
                                shift window when the rule's toggle is on.
                                Skips PAID / FUNDED / CLOSED periods. */}
                            <div
                                className="flex items-center justify-between gap-3 flex-wrap border-t border-[color:var(--asl-border,#E5E7EB)] pt-3"
                                data-testid="admin-settings-tool-p62"
                            >
                                <div className="flex items-start gap-3 flex-1 min-w-[240px]">
                                    <Timer className="h-4 w-4 asl-navy-text mt-0.5 shrink-0" />
                                    <div>
                                        <div className="text-sm font-semibold asl-navy-text">
                                            Recompute Payable (Clamp) — Iter62
                                        </div>
                                        <p className="text-xs text-[color:var(--asl-muted)] mt-0.5">
                                            Sweeps every <strong>CN + IC + AP</strong>{" "}
                                            attendance row and re-derives{" "}
                                            <span className="mono">payable_base_minutes</span>{" "}
                                            using the current shift rule.
                                            When the rule's{" "}
                                            <span className="mono">clamp_worked_hours_to_shift_window</span>{" "}
                                            toggle is on, payable is narrowed to the
                                            effective shift window (per-day override honoured).
                                            When off, payable equals raw worked. The{" "}
                                            <span className="mono">worked_minutes</span>{" "}
                                            column is <strong>never modified</strong> —
                                            biometric truth is preserved for audit.
                                            <br />
                                            <span className="asl-navy-text font-semibold">
                                                Paid / funded / closed payroll periods are
                                                left untouched.
                                            </span>{" "}
                                            <span className="asl-crimson-text font-semibold">
                                                Always dry-run first.
                                            </span>
                                        </p>
                                    </div>
                                </div>
                                <Button
                                    onClick={runP62Preview}
                                    disabled={p62Loading}
                                    className="asl-navy-bg text-white hover:opacity-90"
                                    data-testid="admin-p62-recompute-btn"
                                >
                                    {p62Loading ? "Scanning…" : "Recompute Payable (Clamp)"}
                                </Button>
                            </div>
                            {/* Iter63 — Schedule bonus recompute. Refreshes the
                                per-day-override schedule bonus fields on every
                                non-terminal attendance row. */}
                            <div
                                className="flex items-center justify-between gap-3 flex-wrap border-t border-[color:var(--asl-border,#E5E7EB)] pt-3"
                                data-testid="admin-settings-tool-p63"
                            >
                                <div className="flex items-start gap-3 flex-1 min-w-[240px]">
                                    <Timer className="h-4 w-4 asl-navy-text mt-0.5 shrink-0" />
                                    <div>
                                        <div className="text-sm font-semibold asl-navy-text">
                                            Recompute Schedule Bonus (Iter63)
                                        </div>
                                        <p className="text-xs text-[color:var(--asl-muted)] mt-0.5">
                                            Refreshes the per-day-override{" "}
                                            <span className="mono">schedule_bonus_minutes</span>{" "}
                                            +{" "}
                                            <span className="mono">schedule_bonus_label</span>{" "}
                                            on every CN + IC + AP attendance row.
                                            Fires only on Present rows with the
                                            matching weekday override configured.
                                            Weekend / holiday / absent / on-leave
                                            rows never receive a bonus.
                                            <br />
                                            <span className="asl-navy-text font-semibold">
                                                Paid / funded / closed payroll periods left untouched.
                                            </span>{" "}
                                            <span className="asl-crimson-text font-semibold">
                                                Always dry-run first.
                                            </span>
                                        </p>
                                    </div>
                                </div>
                                <Button
                                    onClick={runP63Preview}
                                    disabled={p63Loading}
                                    className="asl-navy-bg text-white hover:opacity-90"
                                    data-testid="admin-p63-recompute-btn"
                                >
                                    {p63Loading ? "Scanning…" : "Recompute Schedule Bonus"}
                                </Button>
                            </div>
                            <div
                                className="flex items-center justify-between gap-3 flex-wrap border-t border-[color:var(--asl-border,#E5E7EB)] pt-3"
                                data-testid="admin-settings-tool-p83"
                            >
                                <div className="flex items-start gap-3 flex-1 min-w-[240px]">
                                    <RefreshCw className="h-4 w-4 asl-navy-text mt-0.5 shrink-0" />
                                    <div>
                                        <div className="text-sm font-semibold asl-navy-text">
                                            Backfill Iter83 bundling flags (Iter83 P0)
                                        </div>
                                        <p className="text-xs text-[color:var(--asl-muted)] mt-0.5">
                                            Scans every casual weekly payroll period, recomputes{" "}
                                            <span className="mono">is_last_casual_week_of_month</span>{" "}
                                            + <span className="mono">bundles_non_casual_monthly</span>{" "}
                                            using the corrected Iter83 P0 predicate (matches{" "}
                                            <span className="mono">_last7_of_month(end_date)</span>{" "}
                                            — end-day in last 7 days of the month). Periods whose
                                            sibling Non-Casual Monthly is <span className="mono">paid</span>{" "}
                                            get their "Monthly Salaries" section suppressed AND the Fund
                                            Req drops the monthly amount. Idempotent — safe to click again.
                                            <br />
                                            <span className="asl-navy-text font-semibold">
                                                Preview first — shows counts without writing.
                                            </span>
                                        </p>
                                    </div>
                                </div>
                                <Button
                                    onClick={runP83Preview}
                                    disabled={p83Loading}
                                    className="asl-navy-bg text-white hover:opacity-90"
                                    data-testid="admin-p83-backfill-btn"
                                >
                                    {p83Loading ? "Scanning…" : "Backfill Iter83 bundling flags"}
                                </Button>
                            </div>

                            <div
                                className="flex items-center justify-between gap-3 flex-wrap border-t border-[color:var(--asl-border,#E5E7EB)] pt-3"
                                data-testid="admin-settings-tool-p34"
                            >
                                <div className="flex items-start gap-3 flex-1 min-w-[240px]">
                                    <Unlock className="h-4 w-4 asl-navy-text mt-0.5 shrink-0" />
                                    <div>
                                        <div className="text-sm font-semibold asl-navy-text">
                                            IC + CN Bulk Unapprove (Iter60.p34)
                                        </div>
                                        <p className="text-xs text-[color:var(--asl-muted)] mt-0.5">
                                            Bulk-flips every currently-approved <span className="mono">attendance_daily</span>
                                            row on IC Camp and Construction back to <span className="mono">approved=false</span>.
                                            Clock times, worked minutes, status and every other field are preserved — only the
                                            approval flags are touched. Used to re-run approvals after a bulk correction. Each
                                            row lands in the audit log with its batch id. Dry-run first.
                                        </p>
                                    </div>
                                </div>
                                <Button
                                    onClick={runP34Preview}
                                    disabled={p34Loading}
                                    className="asl-navy-bg text-white hover:opacity-90"
                                    data-testid="admin-p34-bulk-unapprove-btn"
                                >
                                    {p34Loading ? "Scanning…" : "Run IC + CN Bulk Unapprove"}
                                </Button>
                            </div>

                            {/* Iter60.p51 cleanup — hard-delete leaked
                                dev-seed workers (CN-P51-01/02/03 and
                                any duplicate copies). Idempotent on the
                                backend; hits a 100-employee safety cap. */}
                            <div
                                className="flex items-center justify-between gap-3 flex-wrap border-t border-[color:var(--asl-border,#E5E7EB)] pt-3"
                                data-testid="admin-settings-tool-p51-cleanup"
                            >
                                <div className="flex items-start gap-3 flex-1 min-w-[240px]">
                                    <Trash2 className="h-4 w-4 asl-crimson-text mt-0.5 shrink-0" />
                                    <div>
                                        <div className="text-sm font-semibold asl-navy-text">
                                            Clean up p51 test workers (Iter60.p51)
                                        </div>
                                        <p className="text-xs text-[color:var(--asl-muted)] mt-0.5">
                                            Hard-deletes every employee whose{" "}
                                            <span className="mono">module_employee_id</span> starts with{" "}
                                            <span className="mono">CN-P51-</span> or whose name starts with{" "}
                                            <span className="mono">"p51 NonCasual"</span> — plus all
                                            downstream <span className="mono">wage_rates</span>,{" "}
                                            <span className="mono">employee_recurring_items</span>,{" "}
                                            <span className="mono">attendance_daily</span>,{" "}
                                            <span className="mono">attendance_events</span>, and{" "}
                                            <span className="mono">payroll_lines</span>. Any{" "}
                                            <span className="mono">payroll_periods</span> named{" "}
                                            <span className="mono">"p51 …"</span> are removed too. Every
                                            real period that lost lines is auto-recomputed so header
                                            totals refresh.
                                            <br />
                                            <span
                                                className={
                                                    p51MatchCount === null
                                                        ? "text-[color:var(--asl-muted)]"
                                                        : p51MatchCount === 0
                                                            ? "text-[color:var(--asl-muted)]"
                                                            : "asl-crimson-text font-semibold"
                                                }
                                                data-testid="admin-p51-match-count"
                                            >
                                                {p51MatchCount === null
                                                    ? "Loading current count…"
                                                    : p51MatchCount === 0
                                                        ? "No p51 test workers found — nothing to clean up."
                                                        : `${p51MatchCount} test worker${p51MatchCount === 1 ? "" : "s"} will be removed.`}
                                            </span>
                                        </p>
                                    </div>
                                </div>
                                <Button
                                    onClick={() => setP51ConfirmOpen(true)}
                                    disabled={p51MatchCount === 0}
                                    className="asl-crimson-bg text-white hover:opacity-90 disabled:opacity-40"
                                    data-testid="admin-p51-cleanup-btn"
                                >
                                    Clean up p51 test workers
                                </Button>
                            </div>
                        </div>
                    </div>
                )}

                {/* Documentation downloads (Admin only) */}
                {isAdmin && (
                    <div className="asl-card p-5" data-testid="admin-settings-documentation-card">
                        <div className="flex items-start gap-3 mb-4">
                            <div className="p-2 asl-navy-bg text-white rounded-md">
                                <FileText className="h-5 w-5" />
                            </div>
                            <div>
                                <h3 className="text-base font-bold asl-navy-text">Documentation</h3>
                                <p className="text-sm text-[color:var(--asl-muted)] mt-1">
                                    Downloadable references for training new joiners and verifying the platform end-to-end.
                                </p>
                            </div>
                        </div>
                        <div className="space-y-3">
                            {visibleDocs.map((d) => (
                                <div
                                    key={d.key}
                                    className="flex items-center justify-between gap-3 flex-wrap border-t border-[color:var(--asl-border,#E5E7EB)] pt-3 first:border-t-0 first:pt-0"
                                    data-testid={`admin-settings-doc-${d.key}`}
                                >
                                    <div className="flex items-start gap-3 flex-1 min-w-[240px]">
                                        <BookOpen className="h-4 w-4 asl-navy-text mt-0.5 shrink-0" />
                                        <div>
                                            <div className="text-sm font-semibold asl-navy-text">{d.label}</div>
                                            <p className="text-xs text-[color:var(--asl-muted)] mt-0.5">
                                                {d.description}
                                            </p>
                                        </div>
                                    </div>
                                    <div className="inline-flex items-center gap-2 shrink-0">
                                        <a
                                            href={d.href}
                                            download
                                            className="inline-flex items-center gap-2 px-3 py-1.5 rounded-md asl-navy-bg text-white text-xs font-semibold hover:opacity-90"
                                            data-testid={d.testid}
                                        >
                                            <Download className="h-3.5 w-3.5" />
                                            Download .docx
                                        </a>
                                        <button
                                            type="button"
                                            className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-md border border-[color:var(--asl-crimson)] text-[color:var(--asl-crimson)] text-xs font-semibold hover:bg-[color:var(--asl-crimson-50,#FDF2F5)] transition-colors"
                                            onClick={() => setConfirmDoc({
                                                key: d.key,
                                                label: d.label,
                                                filename: d.href.replace(/^\//, ""),
                                            })}
                                            data-testid={`admin-settings-doc-delete-${d.key}`}
                                            aria-label={`Delete ${d.label}`}
                                            title="Permanently delete this document from the server"
                                        >
                                            <Trash2 className="h-3.5 w-3.5" />
                                        </button>
                                    </div>
                                </div>
                            ))}
                            {visibleDocs.length === 0 && (
                                <div className="text-xs text-[color:var(--asl-muted)] italic py-3">
                                    No documentation files available. All whitelisted DOCX files have been deleted from the server.
                                </div>
                            )}
                        </div>
                    </div>
                )}
            </section>

            <Dialog open={!!confirmDoc} onOpenChange={(o) => !o && setConfirmDoc(null)}>
                <DialogContent data-testid="admin-doc-delete-dialog">
                    <DialogHeader>
                        <DialogTitle>Delete "{confirmDoc?.label}"?</DialogTitle>
                    </DialogHeader>
                    <div className="text-sm space-y-3">
                        <p>
                            This permanently removes the DOCX file from the server. Users will no longer be able to
                            download it. <span className="font-semibold asl-crimson-text">This cannot be undone.</span>
                        </p>
                        <p className="text-xs text-[color:var(--asl-muted)] mono">
                            File: {confirmDoc?.filename}
                        </p>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setConfirmDoc(null)} disabled={deleting}
                                data-testid="admin-doc-delete-cancel">
                            Cancel
                        </Button>
                        <Button className="asl-crimson-bg text-white" onClick={doDelete} disabled={deleting}
                                data-testid="admin-doc-delete-confirm">
                            {deleting ? "Deleting…" : "Delete permanently"}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Realign Biometric IDs — preview modal */}
            <Dialog open={!!realignPreview} onOpenChange={(o) => !o && setRealignPreview(null)}>
                <DialogContent className="max-w-2xl" data-testid="admin-realign-dialog">
                    <DialogHeader>
                        <DialogTitle>Realign Biometric IDs — preview</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-3 text-sm">
                        <p>
                            The following <span className="font-semibold">{realignPreview?.count || 0}</span> employee(s)
                            have a <span className="mono">biometric_user_id</span> that disagrees with the module prefix.
                            Review the mapping below and click <span className="font-semibold">Apply changes</span> to fix them.
                        </p>
                        <div className="max-h-72 overflow-auto border border-[color:var(--asl-border,#E5E7EB)] rounded-md">
                            <table className="w-full text-xs" data-testid="admin-realign-preview-table">
                                <thead className="asl-navy-bg text-white sticky top-0">
                                    <tr>
                                        <th className="text-left px-3 py-2">Employee</th>
                                        <th className="text-left px-3 py-2">Code</th>
                                        <th className="text-left px-3 py-2">From</th>
                                        <th className="text-left px-3 py-2">To</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {(realignPreview?.changes || []).map((c) => (
                                        <tr key={c.employee_id} className="border-t border-[color:var(--asl-border,#E5E7EB)]">
                                            <td className="px-3 py-2">{c.full_name || "—"}</td>
                                            <td className="px-3 py-2 mono">{c.employee_code}</td>
                                            <td className="px-3 py-2 mono asl-crimson-text">{c.from}</td>
                                            <td className="px-3 py-2 mono font-semibold asl-navy-text">{c.to}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                        {(realignPreview?.safelisted_skipped || []).length > 0 && (
                            <p className="text-xs text-[color:var(--asl-muted)]">
                                {realignPreview.safelisted_skipped.length} safelisted legacy ID(s) will remain untouched:
                                {" "}
                                {realignPreview.safelisted_skipped.map((s, i) => (
                                    <span key={s.employee_id} className="mono">
                                        {i > 0 ? ", " : ""}{s.employee_code}={s.biometric_user_id}
                                    </span>
                                ))}
                                .
                            </p>
                        )}
                    </div>
                    <DialogFooter>
                        <Button
                            variant="outline"
                            onClick={() => setRealignPreview(null)}
                            disabled={realignApplying}
                            data-testid="admin-realign-cancel"
                        >
                            Cancel
                        </Button>
                        <Button
                            className="asl-navy-bg text-white"
                            onClick={applyRealign}
                            disabled={realignApplying}
                            data-testid="admin-realign-apply-btn"
                        >
                            {realignApplying ? "Applying…" : `Apply changes (${realignPreview?.count || 0})`}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Iter60.p27 — AP Attendance Recompute preview modal */}
            <Dialog open={!!p27Preview} onOpenChange={(o) => { if (!o) { setP27Preview(null); setP27ConfirmApply(false); } }}>
                <DialogContent className="max-w-3xl" data-testid="admin-p27-dialog">
                    <DialogHeader>
                        <DialogTitle>AP Attendance Recompute — dry-run preview</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-3 text-sm">
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
                            <div className="rounded-md border border-[color:var(--asl-border,#E5E7EB)] p-2">
                                <div className="text-[color:var(--asl-muted)]">Processed</div>
                                <div className="text-lg font-bold asl-navy-text" data-testid="admin-p27-processed">
                                    {p27Preview?.processed ?? 0}
                                </div>
                            </div>
                            <div className="rounded-md border border-[color:var(--asl-border,#E5E7EB)] p-2">
                                <div className="text-[color:var(--asl-muted)]">Will change</div>
                                <div className="text-lg font-bold asl-crimson-text" data-testid="admin-p27-changed">
                                    {p27Preview?.changed_count ?? 0}
                                </div>
                            </div>
                            <div className="rounded-md border border-[color:var(--asl-border,#E5E7EB)] p-2">
                                <div className="text-[color:var(--asl-muted)]">Already aligned</div>
                                <div className="text-lg font-bold asl-navy-text">
                                    {p27Preview?.unchanged_count ?? 0}
                                </div>
                            </div>
                            <div className="rounded-md border border-[color:var(--asl-border,#E5E7EB)] p-2">
                                <div className="text-[color:var(--asl-muted)]">Rule pointer sync</div>
                                <div className="text-lg font-bold asl-navy-text">
                                    {p27Preview?.stale_rule_pointer_synced ?? 0}
                                </div>
                            </div>
                        </div>
                        <p className="text-xs text-[color:var(--asl-muted)]">
                            Each row below shows the fields that will change on apply. Manual clock times,
                            <span className="mono"> worked_minutes</span>, and locked statuses are preserved.
                        </p>
                        <div className="max-h-80 overflow-auto border border-[color:var(--asl-border,#E5E7EB)] rounded-md">
                            <table className="w-full text-xs" data-testid="admin-p27-results-table">
                                <thead className="asl-navy-bg text-white sticky top-0">
                                    <tr>
                                        <th className="text-left px-3 py-2">Employee</th>
                                        <th className="text-left px-3 py-2">Code</th>
                                        <th className="text-left px-3 py-2">Date</th>
                                        <th className="text-left px-3 py-2">Field</th>
                                        <th className="text-left px-3 py-2">From</th>
                                        <th className="text-left px-3 py-2">To</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {(p27Preview?.sample_diffs || []).flatMap((row) =>
                                        Object.entries(row.diff || {}).map(([field, delta], idx) => (
                                            <tr
                                                key={`${row.employee_id}-${row.work_date}-${field}`}
                                                className="border-t border-[color:var(--asl-border,#E5E7EB)]"
                                                data-testid={`admin-p27-row-${row.employee_id}-${row.work_date}-${field}`}
                                            >
                                                {idx === 0 ? (
                                                    <>
                                                        <td className="px-3 py-2" rowSpan={Object.keys(row.diff).length}>
                                                            {row.employee_name || "—"}
                                                        </td>
                                                        <td className="px-3 py-2 mono" rowSpan={Object.keys(row.diff).length}>
                                                            {row.employee_code || "—"}
                                                        </td>
                                                        <td className="px-3 py-2 mono" rowSpan={Object.keys(row.diff).length}>
                                                            {row.work_date}
                                                        </td>
                                                    </>
                                                ) : null}
                                                <td className="px-3 py-2 mono">{field}</td>
                                                <td className="px-3 py-2 mono asl-crimson-text">
                                                    {String(delta.before)}
                                                </td>
                                                <td className="px-3 py-2 mono font-semibold asl-navy-text">
                                                    {String(delta.after)}
                                                </td>
                                            </tr>
                                        ))
                                    )}
                                </tbody>
                            </table>
                        </div>
                        {p27ConfirmApply && (
                            <div
                                className="rounded-md border border-[color:var(--asl-crimson)] bg-[color:var(--asl-crimson-50,#FDF2F5)] p-3"
                                data-testid="admin-p27-confirm-box"
                            >
                                <p className="text-xs asl-crimson-text font-semibold">
                                    Apply these {p27Preview?.changed_count ?? 0} change(s) to the live database?
                                </p>
                                <p className="text-xs text-[color:var(--asl-muted)] mt-1">
                                    This will overwrite <span className="mono">status</span>,
                                    <span className="mono"> multiplier_applied</span>,
                                    <span className="mono"> is_weekend</span>, and
                                    <span className="mono"> payable_hours</span> on the listed rows. Cannot be undone in
                                    a single click — you'd need another migration to reverse.
                                </p>
                            </div>
                        )}
                    </div>
                    <DialogFooter>
                        <Button
                            variant="outline"
                            onClick={() => { setP27Preview(null); setP27ConfirmApply(false); }}
                            disabled={p27Applying}
                            data-testid="admin-p27-cancel"
                        >
                            Cancel
                        </Button>
                        {p27ConfirmApply ? (
                            <Button
                                className="asl-crimson-bg text-white"
                                onClick={applyP27}
                                disabled={p27Applying}
                                data-testid="admin-p27-apply-confirm-btn"
                            >
                                {p27Applying ? "Applying…" : `Yes — apply ${p27Preview?.changed_count ?? 0} change(s)`}
                            </Button>
                        ) : (
                            <Button
                                className="asl-crimson-bg text-white"
                                onClick={() => setP27ConfirmApply(true)}
                                disabled={p27Applying || (p27Preview?.changed_count || 0) === 0}
                                data-testid="admin-p27-apply-btn"
                            >
                                Apply changes
                            </Button>
                        )}
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Iter60.p39 — Recompute Worked-Minutes preview modal */}
            <Dialog open={!!p39Preview} onOpenChange={(o) => { if (!o) { setP39Preview(null); setP39ConfirmApply(false); } }}>
                <DialogContent className="max-w-3xl" data-testid="admin-p39-dialog">
                    <DialogHeader>
                        <DialogTitle>Worked-Minutes Recompute — dry-run preview</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-3 text-sm">
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
                            <div className="rounded-md border border-[color:var(--asl-border,#E5E7EB)] p-2">
                                <div className="text-[color:var(--asl-muted)]">Processed</div>
                                <div className="text-lg font-bold asl-navy-text" data-testid="admin-p39-processed">
                                    {p39Preview?.processed ?? 0}
                                </div>
                            </div>
                            <div className="rounded-md border border-[color:var(--asl-border,#E5E7EB)] p-2">
                                <div className="text-[color:var(--asl-muted)]">Will change</div>
                                <div className="text-lg font-bold asl-crimson-text" data-testid="admin-p39-changed">
                                    {p39Preview?.changed_count ?? 0}
                                </div>
                            </div>
                            <div className="rounded-md border border-[color:var(--asl-border,#E5E7EB)] p-2">
                                <div className="text-[color:var(--asl-muted)]">Already correct</div>
                                <div className="text-lg font-bold asl-navy-text">
                                    {p39Preview?.unchanged_count ?? 0}
                                </div>
                            </div>
                            <div className="rounded-md border border-[color:var(--asl-border,#E5E7EB)] p-2">
                                <div className="text-[color:var(--asl-muted)]">Modules touched</div>
                                <div className="text-lg font-bold asl-navy-text">
                                    {(p39Preview?.per_module || []).filter((m) => (m.changed || 0) > 0).length}
                                </div>
                            </div>
                        </div>
                        <p className="text-xs text-[color:var(--asl-muted)]">
                            Rows below show <span className="mono">before → after</span> for the canonical worked-minutes
                            formula. Payable hours are re-derived from the new value. On apply, affected payroll periods
                            (draft/prepared) are recomputed automatically.
                        </p>
                        <div className="max-h-80 overflow-auto border border-[color:var(--asl-border,#E5E7EB)] rounded-md">
                            <table className="w-full text-xs" data-testid="admin-p39-results-table">
                                <thead className="asl-navy-bg text-white sticky top-0">
                                    <tr>
                                        <th className="text-left px-3 py-2">Employee</th>
                                        <th className="text-left px-3 py-2">Code</th>
                                        <th className="text-left px-3 py-2">Module</th>
                                        <th className="text-left px-3 py-2">Date</th>
                                        <th className="text-left px-3 py-2">In → Out</th>
                                        <th className="text-right px-3 py-2">Before</th>
                                        <th className="text-right px-3 py-2">After</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {(p39Preview?.sample_diffs || []).map((r) => (
                                        <tr key={r.row_id}
                                            className="border-t border-[color:var(--asl-border,#E5E7EB)]"
                                            data-testid={`admin-p39-row-${r.row_id}`}>
                                            <td className="px-3 py-2">{r.employee_name || "—"}</td>
                                            <td className="px-3 py-2 mono">{r.employee_code || "—"}</td>
                                            <td className="px-3 py-2 mono">{r.module_code || "—"}</td>
                                            <td className="px-3 py-2 mono">{r.work_date}</td>
                                            <td className="px-3 py-2 mono">
                                                {(r.first_clock_in || "").slice(11, 16)} →{" "}
                                                {(r.last_clock_out || "").slice(11, 16)}
                                            </td>
                                            <td className="px-3 py-2 mono asl-crimson-text text-right">{r.before}m</td>
                                            <td className="px-3 py-2 mono font-semibold asl-navy-text text-right">{r.after}m</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                        {p39ConfirmApply && (
                            <div
                                className="rounded-md border border-[color:var(--asl-crimson)] bg-[color:var(--asl-crimson-50,#FDF2F5)] p-3"
                                data-testid="admin-p39-confirm-box"
                            >
                                <p className="text-xs asl-crimson-text font-semibold">
                                    Apply these {p39Preview?.changed_count ?? 0} change(s) and recompute affected payroll periods?
                                </p>
                                <p className="text-xs text-[color:var(--asl-muted)] mt-1">
                                    Overwrites <span className="mono">worked_minutes</span> and <span className="mono">payable_hours</span>
                                    on the listed rows, writes a per-row audit entry, and triggers
                                    <span className="mono"> build_lines_for_period + compute_period_totals</span> on every
                                    draft/prepared payroll period that touches an affected work_date.
                                </p>
                            </div>
                        )}
                    </div>
                    <DialogFooter>
                        <Button
                            variant="outline"
                            onClick={() => { setP39Preview(null); setP39ConfirmApply(false); }}
                            disabled={p39Applying}
                            data-testid="admin-p39-cancel"
                        >
                            Cancel
                        </Button>
                        {p39ConfirmApply ? (
                            <Button
                                className="asl-crimson-bg text-white"
                                onClick={applyP39}
                                disabled={p39Applying}
                                data-testid="admin-p39-apply-confirm-btn"
                            >
                                {p39Applying ? "Applying…" : `Yes — apply ${p39Preview?.changed_count ?? 0} change(s)`}
                            </Button>
                        ) : (
                            <Button
                                className="asl-crimson-bg text-white"
                                onClick={() => setP39ConfirmApply(true)}
                                disabled={p39Applying || (p39Preview?.changed_count || 0) === 0}
                                data-testid="admin-p39-apply-btn"
                            >
                                Apply changes
                            </Button>
                        )}
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Iter60.p45 — Per-day schedule recompute preview modal.
                Same layout as p39, narrowed to IC + CN. */}
            <Dialog open={!!p45Preview} onOpenChange={(o) => { if (!o) { setP45Preview(null); setP45ConfirmApply(false); } }}>
                <DialogContent className="max-w-3xl" data-testid="admin-p45-dialog">
                    <DialogHeader>
                        <DialogTitle>Per-Day Schedule Recompute — dry-run preview (IC + CN)</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-3 text-sm">
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
                            <div className="rounded-md border border-[color:var(--asl-border,#E5E7EB)] p-2">
                                <div className="text-[color:var(--asl-muted)]">Processed</div>
                                <div className="text-lg font-bold asl-navy-text" data-testid="admin-p45-processed">
                                    {p45Preview?.processed ?? 0}
                                </div>
                            </div>
                            <div className="rounded-md border border-[color:var(--asl-border,#E5E7EB)] p-2">
                                <div className="text-[color:var(--asl-muted)]">Will change</div>
                                <div className="text-lg font-bold asl-crimson-text" data-testid="admin-p45-changed">
                                    {p45Preview?.changed_count ?? 0}
                                </div>
                            </div>
                            <div className="rounded-md border border-[color:var(--asl-border,#E5E7EB)] p-2">
                                <div className="text-[color:var(--asl-muted)]">Already correct</div>
                                <div className="text-lg font-bold asl-navy-text">
                                    {p45Preview?.unchanged_count ?? 0}
                                </div>
                            </div>
                            <div className="rounded-md border border-[color:var(--asl-border,#E5E7EB)] p-2">
                                <div className="text-[color:var(--asl-muted)]">Modules touched</div>
                                <div className="text-lg font-bold asl-navy-text">
                                    {(p45Preview?.per_module || []).filter((m) => (m.changed || 0) > 0).length}
                                </div>
                            </div>
                        </div>
                        <p className="text-xs text-[color:var(--asl-muted)]">
                            Only IC + CN rows whose weekday has a{" "}
                            <span className="mono">per_day_overrides</span> entry on the
                            resolved shift rule are considered. Rows in{" "}
                            <span className="asl-navy-text font-semibold">paid</span> payroll
                            periods are left untouched (historical).
                        </p>
                        <div className="max-h-80 overflow-auto border border-[color:var(--asl-border,#E5E7EB)] rounded-md">
                            <table className="w-full text-xs" data-testid="admin-p45-results-table">
                                <thead className="asl-navy-bg text-white sticky top-0">
                                    <tr>
                                        <th className="text-left px-3 py-2">Employee</th>
                                        <th className="text-left px-3 py-2">Code</th>
                                        <th className="text-left px-3 py-2">Module</th>
                                        <th className="text-left px-3 py-2">Date</th>
                                        <th className="text-left px-3 py-2">In → Out</th>
                                        <th className="text-right px-3 py-2">Before</th>
                                        <th className="text-right px-3 py-2">After</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {(p45Preview?.sample_diffs || []).map((r) => (
                                        <tr key={r.row_id}
                                            className="border-t border-[color:var(--asl-border,#E5E7EB)]"
                                            data-testid={`admin-p45-row-${r.row_id}`}>
                                            <td className="px-3 py-2">{r.employee_name || "—"}</td>
                                            <td className="px-3 py-2 mono">{r.employee_code || "—"}</td>
                                            <td className="px-3 py-2 mono">{r.module_code || "—"}</td>
                                            <td className="px-3 py-2 mono">{r.work_date}</td>
                                            <td className="px-3 py-2 mono">
                                                {(r.first_clock_in || "").slice(11, 16)} →{" "}
                                                {(r.last_clock_out || "").slice(11, 16)}
                                            </td>
                                            <td className="px-3 py-2 mono asl-crimson-text text-right">{r.before}m</td>
                                            <td className="px-3 py-2 mono font-semibold asl-navy-text text-right">{r.after}m</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                        {p45ConfirmApply && (
                            <div
                                className="rounded-md border border-[color:var(--asl-crimson)] bg-[color:var(--asl-crimson-50,#FDF2F5)] p-3"
                                data-testid="admin-p45-confirm-box"
                            >
                                <p className="text-xs asl-crimson-text font-semibold">
                                    Apply these {p45Preview?.changed_count ?? 0} change(s) and recompute affected payroll periods?
                                </p>
                            </div>
                        )}
                    </div>
                    <DialogFooter>
                        <Button
                            variant="outline"
                            onClick={() => { setP45Preview(null); setP45ConfirmApply(false); }}
                            disabled={p45Applying}
                            data-testid="admin-p45-cancel"
                        >
                            Cancel
                        </Button>
                        {p45ConfirmApply ? (
                            <Button
                                className="asl-crimson-bg text-white"
                                onClick={applyP45}
                                disabled={p45Applying}
                                data-testid="admin-p45-apply-confirm-btn"
                            >
                                {p45Applying ? "Applying…" : `Yes — apply ${p45Preview?.changed_count ?? 0} change(s)`}
                            </Button>
                        ) : (
                            <Button
                                className="asl-crimson-bg text-white"
                                onClick={() => setP45ConfirmApply(true)}
                                disabled={p45Applying || (p45Preview?.changed_count || 0) === 0}
                                data-testid="admin-p45-apply-btn"
                            >
                                Apply changes
                            </Button>
                        )}
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Iter62 — Reclamp payable-base preview modal.
                Shows the per-module (CN / IC / AP) breakdown of updated
                / unchanged / skipped-terminal rows before applying. */}
            <Dialog
                open={!!p62Preview}
                onOpenChange={(o) => { if (!o) setP62Preview(null); }}
            >
                <DialogContent className="max-w-2xl" data-testid="admin-p62-dialog">
                    <DialogHeader>
                        <DialogTitle>Recompute Payable (Clamp) — dry-run preview</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-3 text-sm">
                        <div className="grid grid-cols-3 gap-3 text-xs">
                            <div className="rounded-md border border-[color:var(--asl-border,#E5E7EB)] p-2">
                                <div className="text-[color:var(--asl-muted)]">Will update</div>
                                <div
                                    className="text-lg font-bold asl-crimson-text"
                                    data-testid="admin-p62-updated"
                                >
                                    {p62Preview?.updated ?? 0}
                                </div>
                            </div>
                            <div className="rounded-md border border-[color:var(--asl-border,#E5E7EB)] p-2">
                                <div className="text-[color:var(--asl-muted)]">Unchanged</div>
                                <div className="text-lg font-bold asl-navy-text">
                                    {p62Preview?.unchanged ?? 0}
                                </div>
                            </div>
                            <div className="rounded-md border border-[color:var(--asl-border,#E5E7EB)] p-2">
                                <div className="text-[color:var(--asl-muted)]">Skipped (paid / funded)</div>
                                <div className="text-lg font-bold asl-navy-text">
                                    {p62Preview?.skipped_terminal ?? 0}
                                </div>
                            </div>
                        </div>
                        <div className="border border-[color:var(--asl-border,#E5E7EB)] rounded-md">
                            <table className="w-full text-xs" data-testid="admin-p62-by-module">
                                <thead className="asl-navy-bg text-white">
                                    <tr>
                                        <th className="text-left px-3 py-2">Module</th>
                                        <th className="text-right px-3 py-2">Will update</th>
                                        <th className="text-right px-3 py-2">Unchanged</th>
                                        <th className="text-right px-3 py-2">Skipped</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {Object.entries(p62Preview?.by_module || {}).map(([code, b]) => (
                                        <tr
                                            key={code}
                                            className="border-t border-[color:var(--asl-border,#E5E7EB)]"
                                            data-testid={`admin-p62-module-${code}`}
                                        >
                                            <td className="px-3 py-2 mono">{code}</td>
                                            <td className="px-3 py-2 mono asl-crimson-text text-right">
                                                {b.updated ?? 0}
                                            </td>
                                            <td className="px-3 py-2 mono text-right">{b.unchanged ?? 0}</td>
                                            <td className="px-3 py-2 mono text-right">{b.skipped_terminal ?? 0}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                        <p className="text-xs text-[color:var(--asl-muted)]">
                            Applying will refresh{" "}
                            <span className="mono">payable_base_minutes</span> on{" "}
                            <strong>{p62Preview?.updated ?? 0}</strong> row(s) and
                            trigger a payroll line rebuild for the{" "}
                            {p62Preview?.periods_refreshed ?? 0} affected draft /
                            prepared period(s).{" "}
                            <span className="mono">worked_minutes</span> is never
                            touched.
                        </p>
                    </div>
                    <DialogFooter>
                        <Button
                            variant="outline"
                            onClick={() => setP62Preview(null)}
                            disabled={p62Applying}
                            data-testid="admin-p62-cancel"
                        >
                            Cancel
                        </Button>
                        <Button
                            className="asl-crimson-bg text-white"
                            onClick={applyP62}
                            disabled={p62Applying || (p62Preview?.updated || 0) === 0}
                            data-testid="admin-p62-apply-btn"
                        >
                            {p62Applying
                                ? "Applying…"
                                : `Apply ${p62Preview?.updated ?? 0} change(s)`}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Iter63 — Schedule bonus dry-run preview */}
            <Dialog
                open={!!p63Preview}
                onOpenChange={(o) => { if (!o) setP63Preview(null); }}
            >
                <DialogContent className="max-w-2xl" data-testid="admin-p63-dialog">
                    <DialogHeader>
                        <DialogTitle>Recompute Schedule Bonus — dry-run preview</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-3 text-sm">
                        <div className="grid grid-cols-3 gap-3 text-xs">
                            <div className="rounded-md border border-[color:var(--asl-border,#E5E7EB)] p-2">
                                <div className="text-[color:var(--asl-muted)]">Will update</div>
                                <div
                                    className="text-lg font-bold asl-crimson-text"
                                    data-testid="admin-p63-updated"
                                >
                                    {p63Preview?.updated ?? 0}
                                </div>
                            </div>
                            <div className="rounded-md border border-[color:var(--asl-border,#E5E7EB)] p-2">
                                <div className="text-[color:var(--asl-muted)]">Unchanged</div>
                                <div className="text-lg font-bold asl-navy-text">
                                    {p63Preview?.unchanged ?? 0}
                                </div>
                            </div>
                            <div className="rounded-md border border-[color:var(--asl-border,#E5E7EB)] p-2">
                                <div className="text-[color:var(--asl-muted)]">Skipped (paid / funded)</div>
                                <div className="text-lg font-bold asl-navy-text">
                                    {p63Preview?.skipped_terminal ?? 0}
                                </div>
                            </div>
                        </div>
                        <div className="border border-[color:var(--asl-border,#E5E7EB)] rounded-md">
                            <table className="w-full text-xs" data-testid="admin-p63-by-module">
                                <thead className="asl-navy-bg text-white">
                                    <tr>
                                        <th className="text-left px-3 py-2">Module</th>
                                        <th className="text-right px-3 py-2">Will update</th>
                                        <th className="text-right px-3 py-2">Unchanged</th>
                                        <th className="text-right px-3 py-2">Skipped</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {Object.entries(p63Preview?.by_module || {}).map(([code, b]) => (
                                        <tr
                                            key={code}
                                            className="border-t border-[color:var(--asl-border,#E5E7EB)]"
                                            data-testid={`admin-p63-module-${code}`}
                                        >
                                            <td className="px-3 py-2 mono">{code}</td>
                                            <td className="px-3 py-2 mono asl-crimson-text text-right">
                                                {b.updated ?? 0}
                                            </td>
                                            <td className="px-3 py-2 mono text-right">{b.unchanged ?? 0}</td>
                                            <td className="px-3 py-2 mono text-right">{b.skipped_terminal ?? 0}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                        <p className="text-xs text-[color:var(--asl-muted)]">
                            Applying will refresh{" "}
                            <span className="mono">schedule_bonus_minutes</span>{" "}
                            +{" "}
                            <span className="mono">schedule_bonus_label</span>{" "}
                            on{" "}
                            <strong>{p63Preview?.updated ?? 0}</strong> row(s)
                            and refresh the{" "}
                            {p63Preview?.periods_refreshed ?? 0} affected
                            draft / prepared period(s).
                        </p>
                    </div>
                    <DialogFooter>
                        <Button
                            variant="outline"
                            onClick={() => setP63Preview(null)}
                            disabled={p63Applying}
                            data-testid="admin-p63-cancel"
                        >
                            Cancel
                        </Button>
                        <Button
                            className="asl-crimson-bg text-white"
                            onClick={applyP63}
                            disabled={p63Applying || (p63Preview?.updated || 0) === 0}
                            data-testid="admin-p63-apply-btn"
                        >
                            {p63Applying
                                ? "Applying…"
                                : `Apply ${p63Preview?.updated ?? 0} change(s)`}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
            <Dialog
                open={p83ConfirmOpen && !!p83Preview}
                onOpenChange={(o) => { if (!o) { setP83ConfirmOpen(false); } }}
            >
                <DialogContent className="max-w-2xl" data-testid="admin-p83-dialog">
                    <DialogHeader>
                        <DialogTitle>Backfill Iter83 bundling flags — dry-run preview</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-3 text-sm">
                        <p className="text-xs text-[color:var(--asl-muted)]">
                            This scan re-evaluates every casual weekly payroll period against
                            the Iter83 P0 predicate. Applying stamps{" "}
                            <span className="mono">bundles_non_casual_monthly</span>{" "}
                            +{" "}
                            <span className="mono">is_last_casual_week_of_month</span>{" "}
                            on each period. Payroll amounts are NOT changed by the stamp
                            itself — you must click Recompute on affected periods (or
                            re-open them) for the totals to refresh.
                        </p>
                        <div className="grid grid-cols-4 gap-3">
                            <div
                                className="rounded-md border border-[color:var(--asl-border,#E5E7EB)] p-3"
                                data-testid="admin-p83-scanned"
                            >
                                <div className="text-xs text-[color:var(--asl-muted)]">Scanned</div>
                                <div className="text-2xl font-bold asl-navy-text mono">
                                    {p83Preview?.periods_scanned ?? 0}
                                </div>
                            </div>
                            <div
                                className="rounded-md border border-[color:var(--asl-border,#E5E7EB)] p-3"
                                data-testid="admin-p83-last-week"
                            >
                                <div className="text-xs text-[color:var(--asl-muted)]">Last-week candidates</div>
                                <div className="text-2xl font-bold asl-navy-text mono">
                                    {p83Preview?.periods_last_week ?? 0}
                                </div>
                            </div>
                            <div
                                className="rounded-md border border-[color:var(--asl-border,#E5E7EB)] p-3"
                                data-testid="admin-p83-suppressed"
                            >
                                <div className="text-xs text-[color:var(--asl-muted)]">
                                    Suppress bundling
                                </div>
                                <div className="text-2xl font-bold asl-crimson-text mono">
                                    {p83Preview?.periods_suppressed ?? 0}
                                </div>
                                <div className="text-[10px] text-[color:var(--asl-muted)]">
                                    (paid sibling)
                                </div>
                            </div>
                            <div
                                className="rounded-md border border-[color:var(--asl-border,#E5E7EB)] p-3"
                                data-testid="admin-p83-bundled"
                            >
                                <div className="text-xs text-[color:var(--asl-muted)]">Keep bundling</div>
                                <div className="text-2xl font-bold asl-navy-text mono">
                                    {p83Preview?.periods_bundled ?? 0}
                                </div>
                                <div className="text-[10px] text-[color:var(--asl-muted)]">
                                    (unpaid / missing)
                                </div>
                            </div>
                        </div>
                        <p className="text-xs text-[color:var(--asl-muted)]">
                            Applying will stamp all{" "}
                            <strong>{p83Preview?.periods_scanned ?? 0}</strong>{" "}
                            casual weekly period(s). The{" "}
                            <strong className="asl-crimson-text">{p83Preview?.periods_suppressed ?? 0}</strong>{" "}
                            suppressed period(s) will surface{" "}
                            <span className="mono">bundles_non_casual_monthly=false</span>{" "}
                            on the next GET — the frontend will hide Monthly Salaries
                            and Fund Req will drop the monthly amount for those weeks.
                        </p>
                    </div>
                    <DialogFooter>
                        <Button
                            variant="outline"
                            onClick={() => setP83ConfirmOpen(false)}
                            disabled={p83Applying}
                            data-testid="admin-p83-cancel"
                        >
                            Cancel
                        </Button>
                        <Button
                            className="asl-crimson-bg text-white"
                            onClick={applyP83}
                            disabled={p83Applying}
                            data-testid="admin-p83-apply-btn"
                        >
                            {p83Applying
                                ? "Applying…"
                                : `Apply to ${p83Preview?.periods_scanned ?? 0} period(s)`}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
            <Dialog open={!!p34Preview} onOpenChange={(o) => { if (!o) { setP34Preview(null); setP34ConfirmApply(false); } }}>
                <DialogContent className="max-w-3xl" data-testid="admin-p34-dialog">
                    <DialogHeader>
                        <DialogTitle>IC + CN Bulk Unapprove — dry-run preview</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-3 text-sm">
                        <div className="grid grid-cols-3 gap-3 text-xs">
                            {(p34Preview?.per_module || []).map((m) => (
                                <div
                                    key={m.module_id}
                                    className="rounded-md border border-[color:var(--asl-border,#E5E7EB)] p-2"
                                    data-testid={`admin-p34-module-${m.code}`}
                                >
                                    <div className="text-[color:var(--asl-muted)]">{m.name || m.code}</div>
                                    <div className="text-lg font-bold asl-crimson-text mono">{m.count} row(s)</div>
                                </div>
                            ))}
                            <div className="rounded-md border-2 border-[color:var(--asl-crimson)] p-2">
                                <div className="text-[color:var(--asl-muted)]">Total</div>
                                <div className="text-lg font-bold asl-crimson-text mono"
                                     data-testid="admin-p34-total">
                                    {p34Preview?.would_unapprove ?? 0}
                                </div>
                            </div>
                        </div>
                        <p className="text-xs text-[color:var(--asl-muted)]">
                            Only <span className="mono">approved</span>, <span className="mono">approved_by</span> and
                            <span className="mono"> approved_at</span> are cleared. Clock times, worked minutes, and
                            adjustments are preserved. Applying writes a per-row audit entry keyed on a batch id.
                        </p>
                        <div className="max-h-64 overflow-auto border border-[color:var(--asl-border,#E5E7EB)] rounded-md">
                            <table className="w-full text-xs" data-testid="admin-p34-sample-table">
                                <thead className="asl-navy-bg text-white sticky top-0">
                                    <tr>
                                        <th className="text-left px-3 py-2">Module</th>
                                        <th className="text-left px-3 py-2">Employee</th>
                                        <th className="text-left px-3 py-2">Code</th>
                                        <th className="text-left px-3 py-2">Date</th>
                                        <th className="text-left px-3 py-2">Status</th>
                                        <th className="text-left px-3 py-2">Approved by</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {(p34Preview?.per_module || []).flatMap((m) =>
                                        (m.sample || []).map((s) => (
                                            <tr key={`${m.code}-${s.id}`}
                                                className="border-t border-[color:var(--asl-border,#E5E7EB)]">
                                                <td className="px-3 py-1.5 mono">{m.code}</td>
                                                <td className="px-3 py-1.5">{s.employee_name || "—"}</td>
                                                <td className="px-3 py-1.5 mono">{s.employee_code || "—"}</td>
                                                <td className="px-3 py-1.5 mono">{s.work_date}</td>
                                                <td className="px-3 py-1.5 mono">{s.status}</td>
                                                <td className="px-3 py-1.5 mono">{s.approved_by || "—"}</td>
                                            </tr>
                                        ))
                                    )}
                                </tbody>
                            </table>
                        </div>
                        {p34ConfirmApply && (
                            <div
                                className="rounded-md border border-[color:var(--asl-crimson)] bg-[color:var(--asl-crimson-50,#FDF2F5)] p-3"
                                data-testid="admin-p34-confirm-box"
                            >
                                <p className="text-xs asl-crimson-text font-semibold">
                                    Unapprove {p34Preview?.would_unapprove ?? 0} row(s) across IC + CN?
                                </p>
                                <p className="text-xs text-[color:var(--asl-muted)] mt-1">
                                    Employees will need to be re-approved individually. Each row lands in the audit log
                                    keyed on a shared batch id so this operation is auditable and reversible manually.
                                </p>
                            </div>
                        )}
                    </div>
                    <DialogFooter>
                        <Button
                            variant="outline"
                            onClick={() => { setP34Preview(null); setP34ConfirmApply(false); }}
                            disabled={p34Applying}
                            data-testid="admin-p34-cancel"
                        >
                            Cancel
                        </Button>
                        {p34ConfirmApply ? (
                            <Button
                                className="asl-crimson-bg text-white"
                                onClick={applyP34}
                                disabled={p34Applying}
                                data-testid="admin-p34-apply-confirm-btn"
                            >
                                {p34Applying ? "Applying…" : `Yes — unapprove ${p34Preview?.would_unapprove ?? 0} row(s)`}
                            </Button>
                        ) : (
                            <Button
                                className="asl-crimson-bg text-white"
                                onClick={() => setP34ConfirmApply(true)}
                                disabled={p34Applying || (p34Preview?.would_unapprove || 0) === 0}
                                data-testid="admin-p34-apply-btn"
                            >
                                Apply changes
                            </Button>
                        )}
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Iter60.p51 cleanup confirmation dialog. */}
            <Dialog open={p51ConfirmOpen} onOpenChange={(o) => { if (!p51Applying) setP51ConfirmOpen(o); }}>
                <DialogContent className="max-w-lg" data-testid="admin-p51-cleanup-dialog">
                    <DialogHeader>
                        <DialogTitle>Clean up p51 test workers?</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-3 text-sm">
                        <p>
                            This will hard-delete all workers whose{" "}
                            <span className="mono">module_employee_id</span> starts with{" "}
                            <span className="mono">CN-P51-</span> and their entire payroll history
                            (wage rates, recurring items, attendance, payroll lines) plus any{" "}
                            payroll periods named <span className="mono">"p51 …"</span>. Every real
                            payroll period that loses lines will be auto-recomputed so the UI header
                            totals refresh.
                        </p>
                        <p className="asl-crimson-text font-semibold">
                            {p51MatchCount === null
                                ? "Preview count unavailable — the server will still cap sweeps at 100 employees."
                                : `${p51MatchCount} test worker${p51MatchCount === 1 ? "" : "s"} will be removed. This cannot be undone.`}
                        </p>
                        {p51Result?.deleted && (
                            <div className="mt-2 rounded-md border border-[color:var(--asl-border,#E5E7EB)] p-3 text-xs">
                                <div className="font-semibold asl-navy-text mb-1">Previous run result</div>
                                <ul className="space-y-0.5 text-[color:var(--asl-muted)]">
                                    {Object.entries(p51Result.deleted).map(([k, v]) => (
                                        <li key={k}>
                                            <span className="mono">{k}</span>: {v}
                                        </li>
                                    ))}
                                    <li>
                                        <span className="mono">periods_recomputed</span>: {p51Result.periods_recomputed?.length || 0}
                                    </li>
                                </ul>
                            </div>
                        )}
                    </div>
                    <DialogFooter>
                        <Button
                            variant="outline"
                            onClick={() => setP51ConfirmOpen(false)}
                            disabled={p51Applying}
                            data-testid="admin-p51-cleanup-cancel"
                        >
                            Cancel
                        </Button>
                        <Button
                            onClick={applyP51Cleanup}
                            disabled={p51Applying}
                            className="asl-crimson-bg text-white hover:opacity-90"
                            data-testid="admin-p51-cleanup-confirm"
                        >
                            {p51Applying ? "Cleaning…" : "Yes, remove them"}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </AppShell>
    );
}
